import {
  IDDocumentType,
  UserVerificationProgress,
  VerificationType,
} from "@amzn/client-workbench-api-model";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import { Logger } from "@/apis/Logger";

export const DOCS_SUBMITTED_KEY = "DocsSubmitted";

/**
 * Once a retry has been initiated (presigned URLs issued), the
 * frontend should not re-render the AI rejection UI even if polling briefly
 * sees the SECONDARY doc still as AI_REJECTED (race condition while the
 * backend processes the retry upload).
 */
export const AI_RETRY_INITIATED_KEY = "AiRetryInitiated";

export const setAiRetryInitiated = (): void => {
  localStorage.setItem(AI_RETRY_INITIATED_KEY, "true");
};

export const getAiRetryInitiated = (): boolean =>
  localStorage.getItem(AI_RETRY_INITIATED_KEY) === "true";

export const clearAiRetryInitiated = (): void => {
  localStorage.removeItem(AI_RETRY_INITIATED_KEY);
};

export const AI_RETRY_CONTEXT_KEY = "AiRetryContext";

export interface AiRetryContext {
  verificationType: VerificationType;
  documentTypes: IDDocumentType[];
}

const VALID_ID_DOCUMENT_TYPES: ReadonlySet<IDDocumentType> =
  new Set<IDDocumentType>([
    "PRIMARY_ID",
    "PRIMARY_ID_RECORDING",
    "SECONDARY_ID",
    "SECONDARY_ID_RECORDING",
    "LIVENESS_RECORDING",
  ]);

export const setAiRetryContext = (context: AiRetryContext): void => {
  localStorage.setItem(AI_RETRY_CONTEXT_KEY, JSON.stringify(context));
};

export const getAiRetryContext = (): AiRetryContext | null => {
  const raw = localStorage.getItem(AI_RETRY_CONTEXT_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as AiRetryContext;
    if (
      !parsed?.verificationType ||
      !Array.isArray(parsed.documentTypes) ||
      parsed.documentTypes.length === 0 ||
      !parsed.documentTypes.every((docType) =>
        VALID_ID_DOCUMENT_TYPES.has(docType)
      )
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearAiRetryContext = (): void => {
  localStorage.removeItem(AI_RETRY_CONTEXT_KEY);
};

/**
 * Mark that the user has submitted their documents. This persists across
 * page refreshes so rivHelper can distinguish "still recording" from
 * "submitted but backend hasn't transitioned to SUBMITTED_VERIFICATION_PENDING yet".
 */
export const setDocsSubmitted = (): void => {
  localStorage.setItem(DOCS_SUBMITTED_KEY, "true");
};

export const getDocsSubmitted = (): boolean =>
  localStorage.getItem(DOCS_SUBMITTED_KEY) === "true";

const isProgressStatusValid = ({
  lightProgress,
  fullProgress,
  autoIdvProgress,
}: {
  lightProgress: UserVerificationProgress;
  fullProgress: UserVerificationProgress;
  autoIdvProgress: UserVerificationProgress;
}) => {
  return (
    (lightProgress.VerificationTypeStatus !== "NOT_APPLICABLE" &&
      fullProgress.VerificationTypeStatus === "NOT_APPLICABLE") ||
    (lightProgress.VerificationTypeStatus === "NOT_APPLICABLE" &&
      fullProgress.VerificationTypeStatus !== "NOT_APPLICABLE") ||
    autoIdvProgress.VerificationTypeStatus === "VERIFICATION_ELIGIBLE"
  );
};

/**
 * Returns a VerificationStatus based on the response from the API with the user progress API.
 * Valid status: "VERIFICATION_ELIGIBLE" | "NOT_APPLICABLE" | "VERIFICATION_COMPLETE" |
 * "SUBMITTED_VERIFICATION_PENDING" | "SUBMISSION_PENDING" | "SUBMISSION_EXPIRED"
 * @param progress Progress data from API.
 */
export const getCurrentRivStep = (
  progress: UserVerificationProgress[]
): VerificationStatus => {
  const lightProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_LIGHT_VERIFICATION"
  );
  const fullProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_FULL_VERIFICATION"
  );
  const autoIdvProgress = progress.find(
    (p) => p.VerificationType === "AUTOMATED_IVV_VERIFICATION"
  );

  // Check if we have all the data to make a decision.
  if (!lightProgress || !fullProgress || !autoIdvProgress) {
    void Logger.error(
      "Could not parse the verification type from the verification API response. Returning NOT_STARTED.",
      undefined,
      { progress }
    );
    return VerificationStatus.NOT_STARTED;
  } else if (
    isDayOneForbidden(progress) ||
    !isProgressStatusValid({ lightProgress, fullProgress, autoIdvProgress })
  ) {
    return VerificationStatus.CONTACT_ITS;
  }

  const isLight =
    lightProgress.VerificationTypeStatus !== "NOT_APPLICABLE" &&
    fullProgress.VerificationTypeStatus === "NOT_APPLICABLE";
  const verificationStatus = isLight
    ? lightProgress.VerificationTypeStatus
    : fullProgress.VerificationTypeStatus;

  // AI quality gate may have flagged one or more artifacts of the
  // active manual IDV. The bundle-readiness check (UserVerification
  // .isReadyForVerification on the backend) treats ``AI_REJECTED`` records
  // as "not ready", so the verification stays in PENDING / SUBMISSION_PENDING
  // until the user re-uploads. Surface ``aiRejectedDocuments`` here as a
  // dedicated state so the widget can show the retry UI rather than the
  // full re-record flow.
  const activeProgress = isLight ? lightProgress : fullProgress;
  if (
    activeProgress.aiRejectedDocuments &&
    activeProgress.aiRejectedDocuments.length > 0 &&
    !getAiRetryInitiated()
  ) {
    return VerificationStatus.AI_REJECTED;
  }

  switch (verificationStatus) {
    case "SUBMITTED_VERIFICATION_REJECTED":
    case "SUBMISSION_EXPIRED":
      return isLight
        ? VerificationStatus.LIGHT_FAIL
        : VerificationStatus.FULL_FAIL;
    case "SUBMITTED_VERIFICATION_SUCCESS":
      return isLight
        ? VerificationStatus.LIGHT_SUCCESS
        : VerificationStatus.FULL_SUCCESS;
    case "SUBMITTED_VERIFICATION_PENDING":
      return isLight
        ? VerificationStatus.LIGHT_PENDING
        : VerificationStatus.FULL_PENDING;
    case "SUBMISSION_PENDING":
    case "VERIFICATION_ELIGIBLE":
      return isLight ? VerificationStatus.LIGHT : VerificationStatus.FULL;
    default:
      if (autoIdvProgress.VerificationTypeStatus === "VERIFICATION_ELIGIBLE") {
        // We return the full verification in this case because we still want the new hire to see the page where AutoIDV is described
        // and with the Continue button leading to the biometrics consents page.
        // If we returned AUTOMATED_IDV_INFLIGHT, this would go straight into biometrics without consent or description.
        return VerificationStatus.FULL;
      }
      void Logger.error(
        `Unhandled verification status '${verificationStatus}'. Returning CONTACT_ITS.`
      );
      return VerificationStatus.CONTACT_ITS;
  }
};

/**
 * Pull the AI-rejected document list off the active manual
 * IDV progress entry. Returns an empty array when the bundle is not in the
 * AI_REJECTED state. The widget feeds the result into the retry UI to
 * decide which document types to re-upload.
 */
export const getAiRejectedDocuments = (
  progress: UserVerificationProgress[]
): NonNullable<UserVerificationProgress["aiRejectedDocuments"]> => {
  const lightProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_LIGHT_VERIFICATION"
  );
  const fullProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_FULL_VERIFICATION"
  );
  const isLight =
    lightProgress &&
    lightProgress.VerificationTypeStatus !== "NOT_APPLICABLE" &&
    fullProgress?.VerificationTypeStatus === "NOT_APPLICABLE";
  const active = isLight ? lightProgress : fullProgress;
  return active?.aiRejectedDocuments ?? [];
};

/**
 * Check if NHs is eligible for starting Day1 onboarding by checking if both full & light RIV statuses are
 * NOT_APPLICABLE. We will retire this method once we get a 403 error from the /user/progress API if NHs are
 * ineligible to start their day 1 onboarding.
 * @param progress Progress data from API.
 */
export const isDayOneForbidden = (
  progress: UserVerificationProgress[]
): boolean => {
  const lightProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_LIGHT_VERIFICATION"
  );
  const fullProgress = progress.find(
    (p) => p.VerificationType === "ON_BOARDING_FULL_VERIFICATION"
  );
  const autoIdvProgress = progress.find(
    (p) => p.VerificationType === "AUTOMATED_IVV_VERIFICATION"
  );

  if (!(fullProgress && lightProgress && autoIdvProgress)) {
    void Logger.error(
      "Cannot parse progress, returning Day One is Forbidden. VerificationType must have ON_BOARDING_LIGHT_VERIFICATION, ON_BOARDING_FULL_VERIFICATION, and AUTOMATED_IVV_VERIFICATION keys.",
      undefined,
      { progress }
    );
    return true;
  }

  /**
   * They're forbidden if their VerificationTypeStatus is 'NOT_APPLICABLE' unless
   * all the EligibilityStatusCodes are 1099, in which case they're not forbidden,
   * there just isn't support for their country at the moment. They'll be presented
   * with a message to contact support as a part of the Identity Verification step.
   * They also will not be forbidden if they are eligible only for Auto IDV.
   */
  return (
    fullProgress.VerificationTypeStatus === "NOT_APPLICABLE" &&
    lightProgress.VerificationTypeStatus === "NOT_APPLICABLE" &&
    autoIdvProgress.VerificationTypeStatus !== "VERIFICATION_ELIGIBLE" &&
    !progress.every((v) => v.EligibilityStatusCode === 1099)
  );
};

export const FAILED_IDV_TYPE = "lastFailedIdvType";

/**
 * Set the last failed IDV type in the localStorage if there is none set yet. This is used for deciding which verification
 * failure message to render if users have failed manual and automated IDVs.
 *
 * @param idvType Manual or Automated IDV
 */
export const setLastFailedIdvType = (idvType: "manual" | "automated"): void => {
  if (!getLastFailedIdvType()) {
    localStorage.setItem(FAILED_IDV_TYPE, idvType);
  }
};

/**
 * Get the last failed IDV type in the localStorage if there is none set yet. This is used for deciding which verification
 * failure message to render if users have failed manual and automated IDVs.
 */
export const getLastFailedIdvType = (): string | undefined => {
  const lastFailedIdvType = localStorage.getItem(FAILED_IDV_TYPE);
  return lastFailedIdvType ? lastFailedIdvType.toLocaleLowerCase() : undefined;
};

/**
 * True if the user have failed automated IDV before. Otherwise, return false.
 */
export const hasFailedAutomatedIdv = (): boolean =>
  getLastFailedIdvType() === "automated";

/**
 * True if the user have failed manual IDV before. Otherwise, return false.
 */
export const hasFailedManualIdv = (): boolean =>
  getLastFailedIdvType() === "manual";
